import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
    ArrowRight,
    Briefcase,
    Coins,
    ShieldCheck,
    CheckCircle,
    Buildings,
    UserFocus,
    Handshake,
    ShareNetwork,
} from "@phosphor-icons/react";
import useSeo from "@/lib/useSeo";
import Brand from "@/components/Brand";

/**
 * `/partner` — Landing page for freelance recruiters, HR consultants and
 * small sub-vendor agencies. Feeds the V1.1 partner waitlist ahead of the
 * V1.3 Talent Marketplace launch.
 *
 * Public (unauth) route. No shared header/footer bindings — we keep the
 * chrome inline so the page stays self-contained and stable if the app
 * shell changes for logged-in flows.
 */

const API = process.env.REACT_APP_BACKEND_URL;

const PARTNER_TYPES = [
    { value: "freelancer",     label: "Freelance recruiter",  desc: "Solo, working from anywhere" },
    { value: "sub_vendor",     label: "Small agency (sub-vendor)", desc: "3-10 person recruitment firm" },
    { value: "hr_consultant",  label: "HR consultant",        desc: "Ex-in-house, running your own book" },
];

const HOW_IT_WORKS = [
    {
        title: "Get live requirements",
        body: "See every open role on NaukriDaddy — India's smart recruitment platform. Filter by function, city, budget. No cold pitches, no gatekeepers.",
        icon: Briefcase,
    },
    {
        title: "Submit candidates. First-in wins.",
        body: "Push profiles from your ATS or straight through NaukriDaddy. First-submitter-wins ownership for 2 weeks. Zero duplicate drama.",
        icon: UserFocus,
    },
    {
        title: "Get paid after joining + 60/90 days",
        body: "Placement fee released after the guarantee window closes. GST invoice auto-generated. No chasing accounts teams.",
        icon: Coins,
    },
];

const WHO_ITS_FOR = [
    { text: "Freelance recruiters", ok: true, desc: "Solo, remote-first, running your own book" },
    { text: "Small agencies as Sub-vendor", ok: true, desc: "3-10 recruiters, hungry for supply" },
    { text: "HR consultants going independent", ok: true, desc: "Ex-in-house, first year on your own" },
    { text: "Job seekers", ok: false, desc: "You want /candidate/signup instead" },
];

const FAQ = [
    {
        q: "When do I get paid?",
        a: "Once your candidate joins the client and completes the guarantee window (60 days default, 90 days for high-risk roles), the fee is released. Full commercials shared with your invite.",
    },
    {
        q: "What's the 60/90-day guarantee?",
        a: "If a candidate you placed exits before the guarantee window closes, the fee is not released — same rule for you as for any full-agency vendor. It keeps quality high and protects the client's trust.",
    },
    {
        q: "What documents do I need to sign up?",
        a: "PAN mandatory. GSTIN mandatory for sub-vendors, optional for freelancers below the ₹20L annual threshold. We'll walk you through KYC when your batch is activated.",
    },
    {
        q: "How do duplicates work?",
        a: "First-submitter-wins with a 2-week claim window per candidate. If another partner has already claimed the profile you're about to submit, we tell you before you waste effort — but never reveal who owns it.",
    },
    {
        q: "What's the commission split?",
        a: "It's per-requirement — set by the client. Typical splits: 60/40 (freelancer) or 55/45 (sub-vendor). Higher for hard-to-hire senior roles.",
    },
    {
        q: "Why the waitlist?",
        a: "We're rolling out in batches while we finalise payout, GST invoicing and dispute-resolution flows. Join now to lock your spot in an early batch — invites go out in the order you signed up.",
    },
];

export default function PartnerLanding() {
    const [params] = useSearchParams();
    const source = params.get("src") || "landing";       // e.g. "poster"
    const referredByCode = params.get("ref") || "";      // referrer user_id from poster QR

    useSeo({
        title: "Earn per placement · NaukriDaddy Partner Program",
        description:
            "Freelance recruiters and small agencies — get access to live requirements, submit candidates, and earn per placement on NaukriDaddy. Join the waitlist for early access.",
        canonical: "https://www.naukridaddy.com/partner",
    });

    const [name, setName]     = useState("");
    const [email, setEmail]   = useState("");
    const [phone, setPhone]   = useState("");
    const [ptype, setPtype]   = useState("freelancer");
    const [city, setCity]     = useState("");
    const [linkedin, setLinkedin] = useState("");
    const [placed5, setPlaced5] = useState(false);
    const [hp, setHp]         = useState(""); // honeypot
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone]     = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name || !email || !phone) {
            toast.error("Fill in name, email and phone to continue.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/api/partner/waitlist`, {
                name, email, phone,
                partner_type: ptype,
                placed_5_plus_last_year: placed5,
                city: city || undefined,
                linkedin_url: linkedin || undefined,
                referred_by_code: referredByCode || undefined,
                source,
                hp_website: hp || undefined,
            });
            setDone(true);
            toast.success("You're on the list — check your inbox for a confirmation.");
            // Google Ads — B2B partner contact request conversion.
            try {
                const { fireGadsConversion } = await import("@/lib/gtagConversions");
                fireGadsConversion("contact_request", { value: 1000, currency: "INR" });
            } catch { /* best-effort */ }
        } catch (ex) {
            const detail = ex?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't submit. Try again in a moment.");
        } finally {
            setSubmitting(false);
        }
    };

    const shareUrl = useMemo(() => `${(typeof window !== "undefined" ? window.location.origin : "")}/partner`, []);
    // V2.0.60 — after `done=true` the waitlist card swaps to a success
    // state, so hero + FAQ "Register as Partner" anchors that just
    // scroll to `#waitlist` look broken to the user (they see the same
    // success card, not a form). This handler resets the form and
    // scrolls, so tapping the CTA a second time actually re-opens the
    // registration surface.
    const openRegistrationForm = (e) => {
        if (done) {
            e.preventDefault();
            setDone(false);
            // Clear the fields so it feels like a fresh registration.
            setName(""); setEmail(""); setPhone(""); setCity("");
            setLinkedin(""); setPlaced5(false); setPtype("freelancer");
            // Wait one frame so React paints the form before scrolling.
            requestAnimationFrame(() => {
                const el = document.getElementById("waitlist");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            });
        }
    };
    const sharePage = async () => {
        try {
            if (navigator.share) {
                await navigator.share({
                    title: "NaukriDaddy Partner Program",
                    text: "Earn per placement — India's smart recruitment marketplace is opening for freelance recruiters.",
                    url: shareUrl,
                });
            } else {
                await navigator.clipboard.writeText(shareUrl);
                toast.success("Link copied to clipboard.");
            }
        } catch {
            // user cancelled or unsupported — no-op
        }
    };

    return (
        <div data-testid="partner-landing-page" className="min-h-screen bg-[#FAFAFA]">
            {/* Minimal top bar */}
            <div className="border-b border-[#E5E5E5] bg-white">
                <div className="max-w-6xl mx-auto flex items-center justify-between px-5 py-3">
                    <Brand size="h-8" to="/" data-testid="partner-logo" />
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            data-testid="partner-share-btn"
                            onClick={sharePage}
                            className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0A0A0A] text-[#0A0A0A] px-4 py-2 transition-colors"
                        >
                            <ShareNetwork size={12} weight="bold" /> Share
                        </button>
                        <Link
                            to="/partner/login"
                            data-testid="partner-signin-link"
                            className="text-xs font-bold uppercase tracking-wider text-[#525252] hover:text-[#0A0A0A]"
                        >
                            Sign in
                        </Link>
                    </div>
                </div>
            </div>

            {/* Hero */}
            <section className="max-w-6xl mx-auto px-5 pt-16 pb-14 sm:pt-24 sm:pb-20 grid lg:grid-cols-5 gap-12 items-start">
                <div className="lg:col-span-3">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#0891B2] mb-4">
                        Naukridaddy Partner Program · Early access
                    </p>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#0A0A0A] leading-[1.05] tracking-tight">
                        Earn ₹25,000 – ₹5,00,000 per hire.<br/>
                        Work from anywhere.<br/>
                        <span className="text-[#0891B2]">Get paid on placement.</span>
                    </h1>
                    <p className="mt-6 text-base sm:text-lg text-[#525252] leading-relaxed max-w-2xl">
                        NaukriDaddy is opening its live requirement pool to freelance recruiters and small agencies. No cold pitches, no dodgy client lists — just curated roles from paying agencies, first-submitter-wins ownership, and GST invoices generated for you.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <a
                            href="#waitlist"
                            onClick={openRegistrationForm}
                            data-testid="partner-cta-primary"
                            className="bg-[#0A0A0A] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 hover:bg-[#1F2937] transition-colors"
                        >
                            Register as Partner <ArrowRight size={14} weight="bold" />
                        </a>
                        <button
                            type="button"
                            onClick={sharePage}
                            data-testid="partner-share-hero"
                            className="border border-[#0A0A0A] text-[#0A0A0A] px-6 py-3 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 hover:bg-[#F5F5F5] transition-colors"
                        >
                            <ShareNetwork size={14} weight="bold" /> Share this page
                        </button>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#525252]">
                        <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} weight="fill" className="text-emerald-600" /> GST invoice auto-generated</span>
                        <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} weight="fill" className="text-emerald-600" /> 60/90-day guarantee windows</span>
                        <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} weight="fill" className="text-emerald-600" /> First-submitter-wins ownership</span>
                    </div>
                </div>

                {/* Waitlist form card */}
                <div className="lg:col-span-2 lg:sticky lg:top-6">
                    <form
                        id="waitlist"
                        onSubmit={submit}
                        data-testid="partner-waitlist-form"
                        className="bg-white border border-[#E5E5E5] p-6 shadow-sm"
                    >
                        {done ? (
                            <div data-testid="partner-waitlist-success" className="space-y-4">
                                <CheckCircle size={36} weight="fill" className="text-emerald-600" />
                                <h2 className="text-2xl font-bold text-[#0A0A0A]">You&apos;re on the list.</h2>
                                <p className="text-sm text-[#525252]">
                                    We&apos;ve sent a confirmation to <strong>{email}</strong>. Watch out for your batch invite in the coming weeks.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={sharePage}
                                        data-testid="partner-success-share"
                                        className="border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F5F5F5] px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 transition-colors"
                                    >
                                        <ShareNetwork size={12} weight="bold" /> Share with a fellow recruiter
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => openRegistrationForm(e)}
                                        data-testid="partner-success-register-another"
                                        className="text-[#0891B2] hover:text-[#0A0A0A] px-2 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 transition-colors"
                                    >
                                        Register another <ArrowRight size={10} weight="bold" />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                                    Join the waitlist
                                </p>
                                <h2 className="text-xl font-bold text-[#0A0A0A] mb-4">
                                    Lock your early-access spot
                                </h2>

                                <div className="space-y-3">
                                    <input
                                        type="text"
                                        required
                                        placeholder="Your full name"
                                        data-testid="partner-input-name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:border-[#0A0A0A] focus:outline-none"
                                    />
                                    <input
                                        type="email"
                                        required
                                        placeholder="Work email"
                                        data-testid="partner-input-email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:border-[#0A0A0A] focus:outline-none"
                                    />
                                    <input
                                        type="tel"
                                        required
                                        placeholder="Phone (with country code)"
                                        data-testid="partner-input-phone"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:border-[#0A0A0A] focus:outline-none"
                                    />
                                    <select
                                        data-testid="partner-input-type"
                                        value={ptype}
                                        onChange={(e) => setPtype(e.target.value)}
                                        className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm bg-white focus:border-[#0A0A0A] focus:outline-none"
                                    >
                                        {PARTNER_TYPES.map((p) => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select>
                                    <div className="grid grid-cols-2 gap-3">
                                        <input
                                            type="text"
                                            placeholder="City"
                                            data-testid="partner-input-city"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            className="border border-[#E5E5E5] px-3 py-2.5 text-sm focus:border-[#0A0A0A] focus:outline-none"
                                        />
                                        <input
                                            type="url"
                                            placeholder="LinkedIn URL"
                                            data-testid="partner-input-linkedin"
                                            value={linkedin}
                                            onChange={(e) => setLinkedin(e.target.value)}
                                            className="border border-[#E5E5E5] px-3 py-2.5 text-sm focus:border-[#0A0A0A] focus:outline-none"
                                        />
                                    </div>
                                    <label className="flex items-start gap-2 text-xs text-[#525252] pt-1 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            data-testid="partner-input-placed5"
                                            checked={placed5}
                                            onChange={(e) => setPlaced5(e.target.checked)}
                                            className="mt-0.5"
                                        />
                                        <span>I&apos;ve placed <strong>5+ candidates</strong> in the last 12 months. (Optional — but helps us prioritise your invite.)</span>
                                    </label>

                                    {/* Honeypot — hidden from humans */}
                                    <input
                                        type="text"
                                        name="hp_website"
                                        value={hp}
                                        onChange={(e) => setHp(e.target.value)}
                                        style={{ position: "absolute", left: "-9999px", width: 0, height: 0, opacity: 0 }}
                                        tabIndex={-1}
                                        autoComplete="off"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="partner-submit-btn"
                                    className="mt-5 w-full bg-[#0A0A0A] text-white px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-[#1F2937] disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
                                >
                                    {submitting ? "Submitting…" : "Join the waitlist"} {!submitting && <ArrowRight size={12} weight="bold" />}
                                </button>
                                <p className="text-[10px] text-[#A3A3A3] text-center mt-3 leading-relaxed">
                                    By joining you agree to receive one confirmation email + occasional launch updates. Zero spam, unsubscribe any time.
                                </p>
                            </>
                        )}
                    </form>
                </div>
            </section>

            {/* How it works */}
            <section className="bg-white border-y border-[#E5E5E5]">
                <div className="max-w-6xl mx-auto px-5 py-14 sm:py-20">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#525252] mb-3">
                        How the partner program works
                    </p>
                    <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] leading-tight mb-10 max-w-2xl">
                        Three steps. No back-and-forth. No dodgy escrow.
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {HOW_IT_WORKS.map(({ title, body, icon: Icon }, i) => (
                            <div key={title} data-testid={`partner-how-${i}`} className="border-l-4 border-[#0891B2] pl-5 py-1">
                                <p className="text-[10px] font-black uppercase tracking-widest text-[#0891B2] mb-2">
                                    Step {i + 1}
                                </p>
                                <div className="flex items-start gap-3 mb-3">
                                    <Icon size={26} weight="duotone" className="text-[#0A0A0A]" />
                                    <h3 className="text-lg font-bold text-[#0A0A0A]">{title}</h3>
                                </div>
                                <p className="text-sm text-[#525252] leading-relaxed">{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Who this is for */}
            <section className="max-w-6xl mx-auto px-5 py-14 sm:py-20">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#525252] mb-3">
                    Who this is for
                </p>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] leading-tight mb-10 max-w-2xl">
                    If you place candidates for a living — this is for you.
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                    {WHO_ITS_FOR.map((item) => (
                        <div
                            key={item.text}
                            data-testid={`partner-who-${item.ok ? "yes" : "no"}`}
                            className={`border ${item.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"} p-4 flex items-start gap-3`}
                        >
                            {item.ok
                                ? <CheckCircle size={22} weight="fill" className="text-emerald-600 mt-0.5" />
                                : <Buildings size={22} weight="fill" className="text-red-500 mt-0.5" />}
                            <div>
                                <p className={`text-sm font-bold ${item.ok ? "text-emerald-900" : "text-red-900"}`}>
                                    {item.text} {item.ok ? "✓" : "×"}
                                </p>
                                <p className={`text-xs mt-0.5 ${item.ok ? "text-emerald-800" : "text-red-800"}`}>{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
                <p className="text-xs text-[#525252] mt-4">
                    Looking for a job? Head over to <Link to="/candidate/signup" className="text-[#0891B2] underline">/candidate/signup</Link> instead.
                </p>
            </section>

            {/* FAQ */}
            <section className="bg-white border-y border-[#E5E5E5]">
                <div className="max-w-4xl mx-auto px-5 py-14 sm:py-20">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#525252] mb-3">
                        Frequently asked
                    </p>
                    <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] leading-tight mb-10">
                        Straight answers, no fluff.
                    </h2>
                    <div className="space-y-4">
                        {FAQ.map((item, i) => (
                            <details
                                key={item.q}
                                data-testid={`partner-faq-${i}`}
                                className="border border-[#E5E5E5] p-5 group hover:border-[#0A0A0A] transition-colors"
                            >
                                <summary className="cursor-pointer font-bold text-[#0A0A0A] text-sm flex items-center justify-between">
                                    <span>{item.q}</span>
                                    <ArrowRight size={14} weight="bold" className="group-open:rotate-90 transition-transform text-[#525252]" />
                                </summary>
                                <p className="mt-3 text-sm text-[#525252] leading-relaxed">{item.a}</p>
                            </details>
                        ))}
                    </div>

                    <div className="mt-10 flex flex-wrap gap-3 items-center">
                        <a
                            href="#waitlist"
                            onClick={openRegistrationForm}
                            data-testid="partner-cta-secondary"
                            className="bg-[#0A0A0A] text-white px-5 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 hover:bg-[#1F2937] transition-colors"
                        >
                            <Handshake size={12} weight="bold" /> Register as Partner
                        </a>
                        <Link
                            to="/about"
                            className="text-[#0891B2] hover:text-[#0A0A0A] text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 transition-colors"
                        >
                            Read our story <ArrowRight size={10} weight="bold" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#0A0A0A] text-white py-10">
                <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-xs text-[#A3A3A3]">
                        © {new Date().getFullYear()} Naukridaddy HR Solutions Private Limited · Delhi, India
                    </p>
                    <div className="flex items-center gap-5 text-xs text-[#A3A3A3]">
                        <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
                        <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                        <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
